import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import useFetchData from '../../../../hooks/UseFetchData'
import Table from '../../../../components/his/invoices/Table'
import PageHeader from '../../../../shared/PageHeader'
import {
  FaArrowCircleLeft,
  FaArrowCircleRight,
  FaFileExcel,
  FaFilePdf,
} from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import moment from 'moment'
import { useSelector } from 'react-redux'
import * as XLSX from 'xlsx'
import { ColumnGroup } from 'primereact/columngroup'
import { Column } from 'primereact/column'
import { Row as ColumnRow } from 'primereact/row'

import CustomButton from '../../../../shared/CustomButton'
import Filters from '../../../../components/his/doctorSummaryReport/Filters'
import { AmiriRegBase64 } from '../../../../assets/fonts/Amiri'
import { AmiriBoldBase64 } from '../../../../assets/fonts/AmiriB'

const toNumber = value => Number(value) || 0

const buildTotalsRow = (data, t) => {
  const rows = Array.isArray(data) ? data : []

  return rows.reduce(
    (acc, row) => ({
      ...acc,
      ConsultationCount:
        acc.ConsultationCount + toNumber(row?.ConsultationCount),
      FollowUpCount: acc.FollowUpCount + toNumber(row?.FollowUpCount),
      TotalCount: acc.TotalCount + toNumber(row?.TotalCount),
    }),
    {
      Date: '',
      PractitionerName: t('Total'),
      Department: '',
      ConsultationCount: 0,
      FollowUpCount: 0,
      TotalCount: 0,
      isGroupHeader: true,
    }
  )
}

const getReportRows = (data, t) => {
  const rows = Array.isArray(data) ? [...data] : []
  if (!rows.length) return []
  return [...rows, buildTotalsRow(rows, t)]
}

const columns = (t, itemWise = false) => [
  {
    header: t('Date'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.Date ? moment(row.Date).format('DD/MM/YYYY') : ''}
      </span>
    ),
  },
  {
    header: t('Practitioner Name'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.PractitionerName || t('Unknown')}
      </span>
    ),
  },
  {
    header: t('Department'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.Department || ''}
      </span>
    ),
  },
  ...(itemWise ? [{
    header: t('Item'),
    bodyCellClass: 'border !border-gray-300',
    body: row => <span>{row?.ItemName || ''}{row?.ItemCode ? ` (${row.ItemCode})` : ''}</span>,
  }] : []),
  {
    header: t('Consultations'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.ConsultationCount ?? 0}
      </span>
    ),
  },
  {
    header: t('Follow Up'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.FollowUpCount ?? 0}
      </span>
    ),
  },
  {
    header: t('Total'),
    bodyCellClass: 'border !border-gray-300',
    body: row => (
      <span className={row?.isGroupHeader ? 'font-bold' : ''}>
        {row?.TotalCount ?? 0}
      </span>
    ),
  },
]

export const exportDrsPatientReportToExcel = (
  data,
  t,
  payloadData = {},
  fileName = 'drs-patient-report.xlsx'
) => {
  const startDate = payloadData?.start_date
    ? moment(payloadData.start_date).format('DD/MM/YYYY')
    : '-'
  const endDate = payloadData?.end_date
    ? moment(payloadData.end_date).format('DD/MM/YYYY')
    : '-'
  const printedOn = moment().format('DD/MM/YYYY HH:mm')
  const rows = getReportRows(data, t)
  const tableRows = rows.map(row => [
    row?.Date ? moment(row.Date).format('DD/MM/YYYY') : '',
    row?.PractitionerName || t('Unknown'),
    row?.Department || '',
    row?.ConsultationCount ?? 0,
    row?.FollowUpCount ?? 0,
    row?.TotalCount ?? 0,
  ])

  const worksheet = XLSX.utils.aoa_to_sheet([
    [t('DRS Patient Report')],
    [`${t('Printed On')}: ${printedOn}`],
    [`${t('Period')}: ${startDate} - ${endDate}`],
    [],
    [
      t('Date'),
      t('Practitioner Name'),
      t('Department'),
      t('Consultations'),
      t('Follow Up'),
      t('Total'),
    ],
    ...tableRows,
  ])

  worksheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 5 } },
  ]
  worksheet['!cols'] = [
    { wch: 16 },
    { wch: 28 },
    { wch: 24 },
    { wch: 18 },
    { wch: 14 },
    { wch: 12 },
  ]
  worksheet['!rows'] = new Array(tableRows.length + 5).fill({ hpt: 22 })

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, t('DRS Patient Report'))
  XLSX.writeFile(workbook, fileName)
}

export const exportDrsPatientReportToPDF = (
  data,
  t,
  payloadData,
  fileName = 'drs-patient-report.pdf'
) => {
  const doc = new jsPDF('landscape')

  doc.addFileToVFS('Amiri-Regular.ttf', AmiriRegBase64)
  doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal')
  doc.addFileToVFS('Amiri-Bold.ttf', AmiriBoldBase64)
  doc.addFont('Amiri-Bold.ttf', 'Amiri', 'bold')
  doc.setFont('Amiri', 'normal')

  const lineHeight = 4
  const pageWidth = doc.internal.pageSize.getWidth()
  const currentDate = moment().format('dddd Do MMMM, YYYY')
  const startDate = payloadData?.start_date
    ? moment(payloadData.start_date).format('DD/MM/YYYY')
    : '-'
  const endDate = payloadData?.end_date
    ? moment(payloadData.end_date).format('DD/MM/YYYY')
    : '-'
  const reportTitle = t('DRS Patient Report')
  const orgName =
    payloadData?.business_en || payloadData?.business_ar || t('Organization')
  const location = payloadData?.address || t('Location')
  const userName = payloadData?.user_name || t('All Users')

  let y = 10

  if (payloadData?.logo) {
    try {
      doc.addImage(payloadData.logo, 'PNG', 10, 8, 25, 25)
    } catch (_) {}
  }

  const leftMargin = payloadData?.logo ? 40 : 14

  doc.setTextColor(0, 0, 0)
  doc.setFontSize(12)
  doc.setFont('Amiri', 'normal')
  doc.text(orgName, leftMargin, y + 5)
  doc.setFontSize(10)
  doc.text(location, leftMargin, y + lineHeight + 5)

  doc.setFontSize(11)
  doc.text(reportTitle, pageWidth - 14, y, { align: 'right' })
  doc.setFontSize(10)
  doc.text(`${t('Printed On')}: ${currentDate}`, pageWidth - 14, y + lineHeight, {
    align: 'right',
  })

  y += lineHeight * 2
  doc.setDrawColor(0)
  doc.setLineWidth(0.1)
  doc.line(14, y, pageWidth - 14, y)
  y += 6

  doc.setFont('Amiri', 'normal')
  doc.text(
    `${t('Daily collection summary for the period from')} ${startDate} ${t('and upto')} ${endDate}`,
    pageWidth - 14,
    y,
    { align: 'right' }
  )
  doc.text(`${t('Report For')}: ${userName}`, pageWidth - 14, y + lineHeight, {
    align: 'right',
  })

  const headers = [[
    t('Date'),
    t('Practitioner Name'),
    t('Department'),
    t('Consultations'),
    t('Follow Up'),
    t('Total'),
  ]]

  const rows = getReportRows(data, t)
  const body = rows.map(row => {
    const isTotal = row?.isGroupHeader
    const totalStyle = {
      fontStyle: 'bold',
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
    }

    return [
      isTotal
        ? { content: '', styles: totalStyle }
        : row?.Date
          ? moment(row.Date).format('DD/MM/YYYY')
          : '',
      isTotal
        ? { content: t('Grand Total'), styles: totalStyle }
        : row?.PractitionerName || t('Unknown'),
      isTotal
        ? { content: '', styles: totalStyle }
        : row?.Department || '',
      isTotal
        ? { content: row?.ConsultationCount ?? 0, styles: totalStyle }
        : row?.ConsultationCount ?? 0,
      isTotal
        ? { content: row?.FollowUpCount ?? 0, styles: totalStyle }
        : row?.FollowUpCount ?? 0,
      isTotal
        ? { content: row?.TotalCount ?? 0, styles: totalStyle }
        : row?.TotalCount ?? 0,
    ]
  })

  autoTable(doc, {
    startY: y + lineHeight * 3,
    head: headers,
    body,
    theme: 'grid',
    headStyles: {
      fillColor: [245, 245, 245],
      textColor: 0,
      fontStyle: 'bold',
      halign: 'left',
      font: 'Amiri',
      fontSize: 8,
      lineWidth: 0.05,
      lineColor: [0, 0, 0],
    },
    bodyStyles: {
      fillColor: [255, 255, 255],
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      font: 'Amiri',
      fontSize: 8,
    },
    columnStyles: {
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'right' },
    },
  })

  doc.save(fileName)
}

const DrsPatientReport = ({ itemWise = false }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const auth = useSelector(state => state.auth)
  const {
    data: requestData,
    executeFetch: requestFetch,
    loading,
  } = useFetchData()

  const { filters: payloadData, setFilters: setPayloadData } = usePersistedFilters(
    itemWise ? 'drs_patient_item_wise_report' : 'drs_patient_report',
    {
      start_date: moment().subtract(1, 'day').toDate(),
      end_date: moment().toDate(),
      department_id: '',
      practitioner: '',
    }
  )

  const requestReport = async (payload = payloadData) => {
    await requestFetch('POST', itemWise ? '/v3/reports/patient/drs-patient-item-wise' : '/v3/reports/patient/drs-patient', {
      ...payload,
      practitioner_id: payload?.practitioner || payload?.practitioner_id || '',
      start_date: moment(payload.start_date).format('YYYY-MM-DD'),
      end_date: moment(payload.end_date).format('YYYY-MM-DD'),
    })
  }

  const reportRows = getReportRows(requestData?.data, t)

  const headerGroup = (
    <ColumnGroup>
      <ColumnRow>
        <Column header={t('Date')} />
        <Column header={t('Practitioner Name')} />
        <Column header={t('Department')} />
        {itemWise && <Column header={t('Item')} />}
        <Column header={t('Consultations')} />
        <Column header={t('Follow Up')} />
        <Column header={t('Total')} />
      </ColumnRow>
    </ColumnGroup>
  )

  return (
    <div className="main-page-container !overflow-y-auto scrollbar">
      <div className="flex items-center justify-between">
        <PageHeader
          title={t(itemWise ? 'DRS Patient Item Wise Report' : 'DRS Patient Report')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <div className="flex items-center w-full justify-between gap-2 mb-2">
        <Filters
          payloadData={payloadData}
          setPayloadData={setPayloadData}
          requestReport={requestReport}
        />
        <div className="flex gap-2">
          <CustomButton
            title={t('Export to Excel')}
            buttonType="success"
            icon={FaFileExcel}
            className="w-fit h-[35px]"
            onClick={() => {
              exportDrsPatientReportToExcel(requestData?.data || [], t, payloadData)
            }}
          />
          <CustomButton
            title={t('Export to PDF')}
            buttonType="danger"
            icon={FaFilePdf}
            className="w-fit h-[35px]"
            onClick={() => {
              exportDrsPatientReportToPDF(requestData?.data || [], t, {
                ...payloadData,
                business_en: auth?.buisnessInfo?.name_en,
                business_ar: auth?.buisnessInfo?.name_ar,
                address: auth?.buisnessInfo?.address,
                logo: auth?.buisnessInfo?.logo,
              })
            }}
          />
        </div>
      </div>

      <Table
        data={reportRows}
        columns={() => columns(t, itemWise)}
        loading={loading}
        headerColumnGroup={headerGroup}
        showGridlines
      />
    </div>
  )
}

export default DrsPatientReport
